import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMyHrContractsByEmployee } from "my-hr/hooks";
import MyTaxApplicationCardInfoItem from "my-tax/components/application-card-info-item/application-card-info-item";
import MyTaxApplicationHeader from "my-tax/components/application-header/application-header";
import MyTaxModifiedStatementCheck from "my-tax/components/modified-statement-check/modified-statement-check";
import TaxCreditApplicationButtonBars from "my-tax/components/tax-credit-application-buttonbars/tax-credit-application-buttonbars";
import MyTaxCreditApplicationCheckboxLine from "my-tax/components/tax-credit-application-checkbox-line/tax-credit-application-checkbox-line";
import MyTaxCreditApplicationErrorBox from "my-tax/components/tax-credit-application-error-box/tax-credit-application-error-box";
import UnderThirtyMothersTaxCreditApplicationPrint from "my-tax/components/under-thirty-mothers-tax-credit-application-print";
import { myTaxUnderThirtyMothersTaxCreditInformationPageRoute } from "my-tax/configs/routes";
import { MY_TAX_CREATE_MOTHER_UNDER_30_DECLARATION } from "my-tax/graphql/mutations/create-tax-declaration-mother-under-30";
import { MY_TAX_GET_MOTHERS_UNDER_THIRTY_DATA } from "my-tax/graphql/queries/get-mothers-under-thirty-data";
import { applyForTaxReliefAllOrPartAmount } from "my-tax/states/apply-for-tax-relief-all-or-part-amount";
import { partDiscountsThirtyMothersState } from "my-tax/states/part-discounts-thirty-mothers-state";
import { taxDeclarationMothersUnder30State } from "my-tax/states/tax-declaration-mothers-under-30-state";
import { underThirtyMothersChildrenEligibleData } from "my-tax/states/under-thirty-mothers-children-eligible-data";
import { OcCard, OcDownload, OcMonthSelect } from "oc/components";
import {
  useOcDependentsData as useDependentsDataDI,
  useOcCurrentUserState,
  useOcSnackbar,
} from "oc/hooks";

interface UseFormDataProps {
  _id: number | string | undefined;
  setSelected: (params: any) => void;
}

const useFormData = ({ _id, setSelected }: UseFormDataProps) => {
  const { t } = useTranslation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [disabledButton, setDisabledButton] = useState(false);
  const [states, setStates] = useRecoilState(taxDeclarationMothersUnder30State);
  const resetStates = useResetRecoilState(taxDeclarationMothersUnder30State);

  const { data, loading, error } = useQuery(
    MY_TAX_GET_MOTHERS_UNDER_THIRTY_DATA,
    {
      fetchPolicy: "cache-and-network",
      skip: !_id,
      variables: {
        _id: Number.parseInt(String(_id ?? "")),
      },
    },
  );

  const result = data?.taxDeclarationMotherUnder30 || null;

  // Sync query result to Recoil state (replaces deprecated onCompleted)
  useEffect(() => {
    if (!loading && result) {
      const newStates = { ...result };
      newStates.dateOfDeclaration = new Date(result?.dateOfDeclaration);
      delete newStates.__typename;
      setStates(newStates);
    }
  }, [loading, result, setStates]);

  const handleChange = ({ key, value }: { key: string; value: any }) => {
    const newState = { ...states, [key]: value };
    setStates(newState);
  };

  const handleStateChange = (newState: typeof states) => {
    setStates(newState);
  };

  const { setOpenMessage } = useOcSnackbar();
  const [saveDeclaration, { loading: mutationLoading }] = useMutation(
    MY_TAX_CREATE_MOTHER_UNDER_30_DECLARATION,
    {
      onCompleted: (data) => {
        const res = data?.createTaxDeclarationMotherUnder30;
        setDisabledButton(false);
        if (res?.result) {
          if (res?.messages?.length > 0) {
            const message = res?.messages[0];
            if (
              message === "A korábban beadott nyilatkozata törlésre került!"
            ) {
              setOpenMessage({
                type: "success",
                message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
              });
              setOpenMessage({
                type: "warning",
                message: t("MY_TAX_PREVIOUS_TAX_DECLARATION_DELETED"),
              });
            }
          } else {
            setOpenMessage({
              type: "success",
              message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
            });
          }
          setSaveErrors([]);
          const params = {
            editable: false,
            tableId: res?.tableId,
            year: states?.dateOfDeclaration?.getFullYear().toString(),
            type: "TaxDecarationMotherUnder30",
            state: "K",
          };
          setSelected(params);
        } else {
          setSaveErrors(res?.messages);
          setDisabledButton(false);
        }
      },
      onError: () => {
        setSaveErrors([t("MY_TAX_DECLARATION_CREATED_FAILED")]);
        setDisabledButton(false);
      },
    },
  );

  useEffect(() => {
    if (!states?.isModifiedDeclaration) {
      handleStateChange({ ...states, noRequestReliefFromMonth: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states?.isModifiedDeclaration]);

  const handleSave = () => {
    setDisabledButton(true);
    const params = { ...states };

    // Remove empty strings to avoid validation errors
    Object.keys(params).forEach((key) => {
      if (params[key as keyof typeof params] === "") {
        delete params[key as keyof typeof params];
      }
    });

    // Remove tax relief amount if not applying for tax relief or applying for all
    if (!params.applyForTaxRelief || params.applyForTaxReliefAllAmount) {
      delete params.applyForTaxReliefAmount;
    }

    saveDeclaration({ variables: params });
  };

  return {
    data: result,
    loading: loading || mutationLoading,
    error,
    states,
    disabledButton,
    handleChange,
    resetStates,
    handleSave,
    handleStateChange,
    saveErrors,
    setSaveErrors,
  };
};

interface MyTaxUnderThirtyMothersTaxCreditApplicationProps {
  fullName: string;
  taxNumber: string;
  useDependentsData?: typeof useDependentsDataDI;
  taxApplicationSaveEnabled: boolean;
  handleRemoveRequest: () => void;
  selected: any;
  setSelected: (params: any) => void;
  isMobile: boolean;
}

const MyTaxUnderThirtyMothersTaxCreditApplication = ({
  fullName,
  taxNumber,
  useDependentsData = useDependentsDataDI,
  taxApplicationSaveEnabled,
  handleRemoveRequest,
  selected,
  setSelected,
  isMobile,
}: MyTaxUnderThirtyMothersTaxCreditApplicationProps) => {
  const editable = selected?.editable;
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();

  const title =
    "Adóelőleg-nyilatkozat a 30 év alatti anyák kedvezményének érvényesítéséről";

  const componentRef = useRef(null);

  const textStyle = { color: "#cc0733" };

  const {
    states,
    loading,
    error,
    saveErrors,
    disabledButton,
    setSaveErrors,
    resetStates,
    handleChange,
    handleSave,
    handleStateChange,
  } = useFormData({
    _id: selected?.tableId,
    setSelected,
  });

  const isModified = selected?.isModified;
  const showModifiedButton = states?.state === "E" ? true : false;
  const showRemoveButton =
    states?.state === "K" || selected?.state === "K" ? true : false;

  const dateOfDeclarationString = new Date(
    states?.dateOfDeclaration,
  ).toLocaleDateString();

  // Date-based logic: use new version if declaration date is 2026-01-01 or later
  const isNewVersion = useMemo(() => {
    if (!states?.dateOfDeclaration) return false;
    const declarationDate = new Date(states.dateOfDeclaration);
    return declarationDate >= new Date("2026-01-01T00:00:00.000Z");
  }, [states?.dateOfDeclaration]);

  const [applyForTaxReliefPartAmount, setApplyForTaxReliefPartAmount] =
    useRecoilState(partDiscountsThirtyMothersState);
  const resetApplyForTaxReliefPartAmount = useResetRecoilState(
    partDiscountsThirtyMothersState,
  );

  useEffect(() => {
    if (
      states?.applyForTaxReliefAmount !== 0 &&
      !applyForTaxReliefPartAmount &&
      !editable
    ) {
      setApplyForTaxReliefPartAmount(true);
    }
  }, [states?.applyForTaxReliefAmount, applyForTaxReliefPartAmount, editable, setApplyForTaxReliefPartAmount]);

  const { data: dependents } = useDependentsData();

  let { contractCode, employeeCode } = useOcCurrentUserState();
  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  let selectedContract = contractsData?.find(
    (item: { code: string }) => item.code === contractCode,
  );

  const print = useReactToPrint({
    content: () => componentRef.current,
  });

  const handlePrint = () => {
    print();
  };

  const handleCancel = () => {
    // Only reset states when in edit mode - when viewing, the states will be
    // overwritten by Apollo data when opening a different declaration
    if (editable) {
      resetApplyForTaxReliefPartAmount();
      resetRadioGroupState();
      resetRadioGroupStateEligibleData();
      resetStates();
    }
    setSaveErrors([]);
  };

  /* Suitable for child */
  const suitableChild = dependents.find(
    (item: any) => item.birthDate, // Removed 2022-12-31 date restriction - eligible for all children
  );

  let childName = suitableChild?.name;
  let childTaxNumber = suitableChild?.taxNumber;
  if (!editable) {
    childName = states?.ownChildName;
    childTaxNumber = states?.ownChildTaxNumber;
  }

  const isSuitableForChildText = isNewVersion ? (
    <>
      a{" "}
      <span style={childName ? textStyle : {}}>{childName || "____"}</span>{" "}
      nevű,{" "}
      <span style={childTaxNumber ? textStyle : {}}>
        {childTaxNumber || "____"}
      </span>{" "}
      adóazonosító jelű vér szerinti vagy örökbefogadott gyermekre tekintettel vagyok jogosult,
    </>
  ) : (
    <>
      a 2022. december 31. után született{" "}
      <span style={childName ? textStyle : {}}>{childName || "____"}</span>{" "}
      nevű,{" "}
      <span style={childTaxNumber ? textStyle : {}}>
        {childTaxNumber || "____"}
      </span>{" "}
      adóazonosító jelű gyermekre tekintettel vagyok jogosult.
    </>
  );

  /* Suitable for fetus */
  const suitableForFetusdList = dependents.filter(
    (item: any) =>
      new Date(item.conceptionDate) > new Date("2022-05-31T00:00:00.000Z"),
  );

  const fetus = dependents.find(
    (item: any) =>
      new Date(item.conceptionDate) > new Date("2022-05-31T00:00:00.000Z"),
  );

  let fetusYear = new Date(fetus?.conceptionDate).getFullYear();
  let fetusMonth = new Date(fetus?.conceptionDate).getMonth() + 1;
  if (!editable) {
    fetusYear = states?.pregnancy91thDayYear;
    fetusMonth = states?.pregnancy91thDayMonth;
  }

  const isSuitableForFetusText = (
    <>
      magzat után vagyok jogosult, a várandósság 91. napját{" "}
      <span style={fetusYear ? textStyle : {}}>{fetusYear || "____"}</span> év{" "}
      <span style={fetusMonth ? textStyle : {}}>{fetusMonth || "____"}</span>{" "}
      hónapban töltöttem be.
    </>
  );

  /* Suitable for adopted */
  const adoptedChild = dependents.find((item: any) => {
    if (item.dependentType2 === "O") { // Removed 2022-12-31 date restriction for adopted children
      if (!item.birthDate) return false;
      const today = new Date();
      const birthDate = new Date(item.birthDate);
      if (birthDate.toString() === "Invalid Date") return false;
      const age = today.getFullYear() - birthDate.getFullYear();
      return age < 18;
    }
    return false;
  });

  let adoptedChildName = adoptedChild?.name;
  let adoptedChildTaxNumber = adoptedChild?.taxNumber;
  if (!editable) {
    adoptedChildName = states?.adoptedChildName;
    adoptedChildTaxNumber = states?.adoptedChildTaxNumber;
  }

  const isSuitableForAdoptedText = (
    <>
      a 2022. december 31. után örökbefogadott{" "}
      <span style={adoptedChildName ? textStyle : {}}>
        {adoptedChildName || "____"}
      </span>{" "}
      nevű,{" "}
      <span style={adoptedChildTaxNumber ? textStyle : {}}>
        {adoptedChildTaxNumber || "____"}
      </span>{" "}
      adóazonosító jelű gyermekre tekintettel vagyok jogosult.
    </>
  );

  const [radioGroupState, setRadioGroupState] = useRecoilState(
    applyForTaxReliefAllOrPartAmount,
  );

  const resetRadioGroupState = useResetRecoilState(
    applyForTaxReliefAllOrPartAmount,
  );

  const [radioGroupStateEligibleData, setRadioGroupStateEligibleData] =
    useRecoilState(underThirtyMothersChildrenEligibleData);

  const resetRadioGroupStateEligibleData = useResetRecoilState(
    underThirtyMothersChildrenEligibleData,
  );

  const handleRadioGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "allDiscountsThirtyMothers") {
      const newStates = {
        ...states,
        applyForTaxReliefAllAmount: true,
        applyForTaxRelief: false,
        applyForTaxReliefAmount: 0,
      };
      setApplyForTaxReliefPartAmount(false);
      handleStateChange(newStates);
    } else {
      const newStates = {
        ...states,
        applyForTaxReliefAllAmount: false,
        applyForTaxRelief: true,
        applyForTaxReliefAmount: Number.parseFloat(e.target.value),
      };
      setApplyForTaxReliefPartAmount(true);
      handleStateChange(newStates);
    }
    setRadioGroupState(e.target.value);
  };

  useEffect(() => {
    if (!editable && states?.applyForTaxReliefAllAmount) {
      setRadioGroupState("allDiscountsThirtyMothers");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, states?.applyForTaxReliefAllAmount]);

  useEffect(() => {
    if (!editable && states?.applyForTaxRelief) {
      setRadioGroupState("partDiscountsThirtyMothers");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, states?.applyForTaxRelief]);

  const handleRadioGroupChangeEligibleData = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.value === "isSuitableForChild") {
      const newStates = {
        ...states,
        eligibleOnOwnChild: true,
        eligibleOnFetus: false,
        eligibleOnAdoptedChild: false,
        ownChildName: childName,
        ownChildTaxNumber: childTaxNumber,
        pregnancy91thDayYear: "",
        pregnancy91thDayMonth: "",
        adoptedChildName: "",
        adoptedChildTaxNumber: "",
      };
      handleStateChange(newStates);
    }
    if (e.target.value === "isSuitableForFetus") {
      const newStates = {
        ...states,
        eligibleOnOwnChild: false,
        eligibleOnFetus: true,
        eligibleOnAdoptedChild: false,
        pregnancy91thDayYear: fetusYear.toString(),
        pregnancy91thDayMonth: fetusMonth.toString(),
        ownChildName: "",
        ownChildTaxNumber: "",
        adoptedChildName: "",
        adoptedChildTaxNumber: "",
      };
      handleStateChange(newStates);
    }
    if (e.target.value === "isSuitableForAdopted") {
      const newStates = {
        ...states,
        eligibleOnOwnChild: false,
        eligibleOnFetus: false,
        eligibleOnAdoptedChild: true,
        adoptedChildName: adoptedChildName,
        adoptedChildTaxNumber: adoptedChildTaxNumber,
        pregnancy91thDayYear: "",
        pregnancy91thDayMonth: "",
        ownChildName: "",
        ownChildTaxNumber: "",
      };
      handleStateChange(newStates);
    }

    setRadioGroupStateEligibleData(e.target.value);
  };

  useEffect(() => {
    if (!editable && states?.eligibleOnOwnChild) {
      setRadioGroupStateEligibleData("isSuitableForChild");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, states?.eligibleOnOwnChild]);

  useEffect(() => {
    if (!editable && states?.eligibleOnFetus) {
      setRadioGroupStateEligibleData("isSuitableForFetus");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, states?.eligibleOnFetus]);

  useEffect(() => {
    if (!editable && states?.eligibleOnAdoptedChild) {
      setRadioGroupStateEligibleData("isSuitableForAdopted");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, states?.eligibleOnAdoptedChild]);

  useEffect(() => {
    if (isModified && !states?.isModifiedDeclaration) {
      handleStateChange({ ...states, isModifiedDeclaration: true });
    }
  }, [isModified, states?.isModifiedDeclaration]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScrollToError = () => {
    const errorElement = document.getElementById("errorBox");
    if (errorElement) {
      window.scrollTo({
        top: 300,
        behavior: "smooth",
      });
    }
  };
  useLayoutEffect(() => {
    handleScrollToError();
  }, [saveErrors, history.action]);

  useEffect(() => {
    // Only reset when creating a NEW declaration (editable=true AND no tableId)
    // This prevents resetting when viewing a saved declaration, even if Recoil
    // hasn't finished restoring the selected state from sessionStorage yet
    if (editable && !selected?.tableId) {
      resetStates();
    }
    if (history.action === "POP" && editable) {
      return handleCancel();
    }
  }, []); //eslint-disable-line

  return (
    <>
      <Box
        style={{
          marginBottom: "-15px",
        }}
      >
        <Box
          sx={{
            display: "none",
            displayPrint: "block",
            paddingRight: "85px",
          }}
        >
          <UnderThirtyMothersTaxCreditApplicationPrint
            ref={componentRef}
            fullName={fullName}
            taxNumber={taxNumber}
            isSuitable={states?.isEligible}
            childName={childName}
            childTaxNumber={childTaxNumber}
            isSuitableForChild={states?.eligibleOnOwnChild}
            isSuitableForFetus={states?.eligibleOnFetus}
            isSuitableForAdopted={states?.eligibleOnAdoptedChild}
            allDiscountsThirtyMothers={states?.applyForTaxReliefAllAmount}
            isNoneApplyStartMonthThirtyMothers={
              states?.noRequestReliefFromMonth
            }
            isModifiedApplication={states?.isModifiedDeclaration}
            underThirtyMothersAmountDiscount={states?.applyForTaxReliefAmount}
            partDiscountsThirtyMothers={applyForTaxReliefPartAmount}
            employerName={selectedContract?.company?.name || ""}
            employerTaxNumber={selectedContract?.company?.taxNumber || ""}
            fetusYear={fetusYear?.toString()}
            fetusMonth={fetusMonth?.toString()}
            adoptedChildName={adoptedChildName}
            adoptedChildTaxNumber={adoptedChildTaxNumber}
            applicationDate={dateOfDeclarationString}
            isNewVersion={isNewVersion}
          />
        </Box>
        <MyTaxApplicationHeader
          title={title}
          onCancel={handleCancel}
          isMobile={isMobile}
        />
        <Box height="16px" />
        <Box display="flex" justifyContent="space-around">
          <Box
            style={{
              padding: "7px",
              width: "260px",
              borderRadius: "4px",
            }}
          >
            <OcDownload
              iconType="document"
              label="Tájékoztató"
              actionIconType="url"
              href={"#" + myTaxUnderThirtyMothersTaxCreditInformationPageRoute}
              target="_blank"
              isSmall={true}
            />
          </Box>
        </Box>
        <Box height="16px" />
        {saveErrors.length > 0 && (
          <Box id="errorBox">
            <MyTaxCreditApplicationErrorBox error={saveErrors} />
          </Box>
        )}
        <OcCard
          label="I. A nyilatkozatot adó magánszemély"
          loading={loading}
          error={!!error}
        >
          <Box height="52px" />
          <MyTaxApplicationCardInfoItem
            label="Dátum"
            value={dateOfDeclarationString}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Dolgozó neve"
            value={fullName}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Adóazonosító jele"
            value={taxNumber}
            isMobile={isMobile}
          />
          <Box height="15px" />
          <MyTaxModifiedStatementCheck
            label={t("MY_TAX_MODIFIED_APPLICATION")}
            name="isModifiedDeclaration"
            value={states?.isModifiedDeclaration}
            onChange={handleChange}
            disabled={!editable || isModified}
            isMobile={isMobile}
          />
          <Box height="15px" />
          <MyTaxCreditApplicationCheckboxLine
            id="isSuitable"
            prefix="1."
            type="checkbox"
            checked={states?.isEligible}
            checkboxDisabled={!editable}
            onChange={(e) =>
              handleChange({ key: "isEligible", value: e.target.checked })
            }
            text={isNewVersion ? "Nyilatkozom, hogy a 30 év alatti anyák kedvezményére a 2. pont szerinti gyermekre, magzatra tekintettel jogosult vagyok." : "Nyilatkozom, hogy a 30 év alatti anyák kedvezményére, a 2. pont szerinti gyermekre, magzatra tekintettel jogosult vagyok."}
            editable={editable}
          />
          <Box height="36px" />
          <Box display="flex">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              2.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              A 30 év alatti anyák kedvezményére
            </Typography>
          </Box>
          <Box height="10px" />
          <Box paddingLeft="30px">
            <FormControl
              component="fieldset"
              sx={{
                display: "flex",
              }}
            >
              <Box height="8px" />
              <RadioGroup
                value={radioGroupStateEligibleData}
                aria-label="gender"
                name="controlled-radio-buttons-group"
                onChange={handleRadioGroupChangeEligibleData}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body1"
                    color={editable ? "primary" : "info"}
                  >
                    a)
                  </Typography>
                  <Box width="16px" />
                  <FormControlLabel
                    value="isSuitableForChild"
                    control={
                      <Radio
                        id="isSuitableForChild"
                        style={{
                          color:
                            !editable || !childTaxNumber
                              ? theme.palette.gray60.main
                              : theme.palette.darkBase.main,
                        }}
                        disabled={
                          !suitableChild || !editable || !childTaxNumber
                        }
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        color={
                          !editable ||
                            !suitableChild ||
                            !editable ||
                            !childTaxNumber
                            ? "info"
                            : "primary"
                        }
                        style={{ alignItems: "center" }}
                      >
                        {isSuitableForChildText}
                      </Typography>
                    }
                  />
                </Box>
                <Box height="10px" />
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="body1"
                    color={editable ? "primary" : "info"}
                  >
                    b)
                  </Typography>

                  <Box width="16px" />
                  <FormControlLabel
                    value="isSuitableForFetus"
                    control={
                      <Radio
                        id="isSuitableForFetus"
                        style={{
                          color:
                            !editable || suitableForFetusdList?.length === 0
                              ? theme.palette.gray60.main
                              : theme.palette.darkBase.main,
                        }}
                        disabled={
                          suitableForFetusdList?.length === 0 || !editable
                        }
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        color={
                          suitableForFetusdList?.length === 0 || !editable
                            ? "info"
                            : "primary"
                        }
                        style={{ alignItems: "center" }}
                      >
                        {isSuitableForFetusText}
                      </Typography>
                    }
                  />
                </Box>
                {!isNewVersion && (
                  <>
                    <Box height="10px" />
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body1"
                        color={editable ? "primary" : "info"}
                      >
                        c)
                      </Typography>

                      <Box width="16px" />
                      <FormControlLabel
                        value="isSuitableForAdopted"
                        control={
                          <Radio
                            id="isSuitableForAdopted"
                            style={{
                              color:
                                !editable || !adoptedChildTaxNumber
                                  ? theme.palette.gray60.main
                                  : theme.palette.darkBase.main,
                            }}
                            disabled={
                              !adoptedChild || !editable || !adoptedChildTaxNumber
                            }
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            color={
                              !adoptedChild || !editable || !adoptedChildTaxNumber
                                ? "info"
                                : "primary"
                            }
                            style={{ alignItems: "center" }}
                          >
                            {isSuitableForAdoptedText}
                          </Typography>
                        }
                      />
                    </Box>
                  </>
                )}
              </RadioGroup>
            </FormControl>
          </Box>
          {!isNewVersion && (
            <>

              <Box height="36px" />
              <Box display="flex">
                <Typography variant="body1" color={editable ? "primary" : "info"}>
                  3.
                </Typography>
                <Box width="16px" />
                <Typography variant="body1" color={editable ? "primary" : "info"}>
                  Kérem a kedvezményt az adóelőleg megállapítása során:
                </Typography>
              </Box>
              <Box height="5px" />
              <Box marginLeft="32px">
                <FormControl
                  component="fieldset"
                  sx={{
                    display: "flex",
                  }}
                >
                  <Box height="8px" />
                  <RadioGroup
                    value={radioGroupState}
                    aria-label="gender"
                    name="controlled-radio-buttons-group"
                    onChange={handleRadioGroupChange}
                  >
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body1"
                        color={editable ? "primary" : "info"}
                      >
                        a)
                      </Typography>
                      <Box width="16px" />
                      <FormControlLabel
                        value="allDiscountsThirtyMothers"
                        control={
                          <Radio
                            id="allDiscountsThirtyMothers"
                            style={{
                              color: !editable
                                ? theme.palette.gray60.main
                                : theme.palette.darkBase.main,
                            }}
                            disabled={!editable}
                          />
                        }
                        label={
                          <Typography
                            variant="body1"
                            color={editable ? "primary" : "info"}
                            style={{ alignItems: "center" }}
                          >
                            Teljes összegben figyelembe venni.
                          </Typography>
                        }
                      />
                    </Box>
                    <Box height="10px" />
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body1"
                        color={editable ? "primary" : "info"}
                      >
                        b)
                      </Typography>

                      <Box width="16px" />
                      <FormControlLabel
                        value="partDiscountsThirtyMothers"
                        control={
                          <Radio
                            id="partDiscountsThirtyMothers"
                            style={{
                              color: !editable
                                ? theme.palette.gray60.main
                                : theme.palette.darkBase.main,
                            }}
                            disabled={!editable}
                          />
                        }
                        label={
                          <MyTaxCreditApplicationCheckboxLine
                            id="marriageAmount"
                            text="forint összegben kívánom igénybe venni."
                            checked={
                              radioGroupState === "partDiscountsThirtyMothers"
                            }
                            textInput={true}
                            textInputType="number"
                            value={
                              states?.applyForTaxReliefAmount !== 0
                                ? states?.applyForTaxReliefAmount
                                : ""
                            }
                            label={t("OC_AMOUNT")}
                            onChangeInput={(e) =>
                              handleChange({
                                key: "applyForTaxReliefAmount",
                                value: parseFloat(
                                  e.target.value.replace(/[^0-9]/g, ""),
                                ),
                              })
                            }
                            textInputDisabled={!editable}
                            isMobile={isMobile}
                            editable={editable}
                          />
                        }
                      />
                    </Box>
                  </RadioGroup>
                </FormControl>
              </Box>
            </>
          )}
          <Box height="36px" />
          <Box
            style={{
              color: !states?.isModifiedDeclaration
                ? theme.palette.gray60.main
                : undefined,
            }}
          >
            <Box display="flex" alignItems={isMobile ? "flex-start" : "center"}>
              <Typography
                variant="body1"
                color={
                  editable && states?.isModifiedDeclaration ? "primary" : "info"
                }
              >
                {!isNewVersion ? "4." : "3."}
              </Typography>
              <Box width="16px" />
              <Box
                display="flex"
                alignItems={isMobile ? "flex-start" : "center"}
                flexDirection={isMobile ? "column" : "row"}
              >
                <Typography
                  variant="body1"
                  color={
                    editable && states?.isModifiedDeclaration
                      ? "primary"
                      : "info"
                  }
                >
                  Nyilatkozom, hogy a 30 év alatti anyák kedvezményét
                </Typography>
                <Box
                  marginLeft={isMobile ? "0px" : "16px"}
                  marginRight="16px"
                  width={isMobile ? "100%" : "auto"}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "4px",
                  }}
                >
                  <OcMonthSelect
                    id="start-of-application-select"
                    width="160px"
                    label={t("MY_TAX_TAX_CREDIT_MONTH")}
                    selectedMonth={states?.noRequestReliefFromMonth}
                    onSelectMonth={(e) =>
                      handleChange({
                        key: "noRequestReliefFromMonth",
                        value: e,
                      })
                    }
                    disabled={!editable || !states?.isModifiedDeclaration}
                    monthList={[
                      { index: "1", name: "Január" },
                      { index: "2", name: "Február" },
                      { index: "3", name: "Március" },
                      { index: "4", name: "Április" },
                      { index: "5", name: "Május" },
                      { index: "6", name: "Június" },
                      { index: "7", name: "Július" },
                      { index: "8", name: "Augusztus" },
                      { index: "9", name: "Szeptember" },
                      { index: "10", name: "Október" },
                      { index: "11", name: "November" },
                      { index: "12", name: "December" },
                    ]}
                    startMonth={1}
                  />
                </Box>
                <Typography
                  variant="body1"
                  color={
                    editable && states?.isModifiedDeclaration
                      ? "primary"
                      : "info"
                  }
                >
                  hónaptól kezdődően nem (erre a hónapra sem) kívánom igénybe
                  venni.
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box height="16px" />
        </OcCard>
        <Box height="16px" />
        <TaxCreditApplicationButtonBars
          taxApplicationSaveEnabled={taxApplicationSaveEnabled && editable}
          cancelData={handleCancel}
          handlePrint={handlePrint}
          saveData={handleSave}
          editable={editable}
          disabledButton={disabledButton}
          handleRemoveRequest={handleRemoveRequest}
          showModifiedButton={showModifiedButton}
          showRemoveButton={showRemoveButton}
          isModified={isModified}
          isModifiedDeclaration={states?.isModifiedDeclaration}
          isMobile={isMobile}
        />
      </Box>
    </>
  );
};

export default MyTaxUnderThirtyMothersTaxCreditApplication;
